import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  authHeaders,
  buildODataUrl,
  getODataRows,
  getExplicitODataTotalCount,
  resolveODataTotalCount,
  exportToExcel,
} from '../listPageStyles';
import { buildFilterClause, escapeODataString } from './filterModel';

const FULL_FETCH_PAGE_SIZE = 500;
const LOAD_MORE_BATCH = 200;
// Stabil default referanslar — inline `{}`/`[]` default'ları her render yeni kimlik üretip
// fetchData/effect'leri sonsuz döngüye sokabiliyor (ERR_INSUFFICIENT_RESOURCES).
const EMPTY_OBJ = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);

/**
 * c360 list veri + filtre + gruplama motoru.
 *
 * Konfig:
 *   apiBase           — '/api/customers' gibi OData taban URL'i
 *   columnFilterMeta  — { columnId: { type, odataField, valueFormat?, options? } }
 *   groupByOdata      — { groupByField: { field, type: 'number'|'guid'|'string' } }
 *   searchFields      — serbest metin aramasının tarayacağı OData alanları (string[])
 *   defaultSortField  — başlangıç sıralama alanı (OData adı), varsayılan 'TextId'
 *   defaultKanbanGroup— kanban'da açık gruplama yokken kullanılacak alan (örn. 'status')
 *   getKeyLabel       — (field, row) => { key, label }  (gruplama etiketleri için; sayfa verir)
 *   seedKanbanBuckets — (groupField) => [{ key, label }]  (boş kanban kolonları için; opsiyonel)
 *   ready             — false ise fetch ertelenir (örn. lookup'lar yüklenene kadar)
 *
 * onError(message) — hata bildirimi (toast) için opsiyonel callback.
 */
export function useC360List({
  apiBase,
  columnFilterMeta = EMPTY_OBJ,
  groupByOdata = EMPTY_OBJ,
  searchFields = EMPTY_ARR,
  defaultSortField = 'TextId',
  defaultSortDir = 'asc',
  defaultKanbanGroup = null,
  getKeyLabel,
  seedKanbanBuckets,
  ready = true,
  // Her zaman uygulanan sabit OData $filter cümlesi (örn. 'Type eq 0'). Kullanıcı
  // filtreleri/arama bununla VE'lenir; kullanıcı temizleyemez. Boş = etkisiz.
  baseFilter = '',
  // $select: düz liste görünümünde çekilecek OData alanları (payload/serileştirme düşürür).
  // Boş/undefined → tüm entity (geriye dönük uyumlu). Grup/kanban/chart/export TAM veri
  // gerektirdiğinden bu alanlar orada uygulanmaz. Sayfa `buildSelectFields` ile üretmeli
  // (görünür kolonların odataField'leri + 'Id' + sortField); STABİL referans olmalı (useMemo).
  selectFields = EMPTY_ARR,
  // $expand: kolonların ihtiyaç duyduğu alt koleksiyonlar (örn. iade listesinde
  // 'ReturnDetails($expand=Allocations)' — Kalem/Miktar/İlişkili Alış Fat. kolonları satır başına
  // ikinci istek atmadan TEK ATIMDA dolsun diye). Boş = etkisiz (geriye dönük uyumlu).
  // NOT: $expand ile $select birlikte kullanılmamalı — expand edilen nav property $select'te
  // yoksa sunucu onu düşürür. Expand veren sayfa selectFields VERMEZ.
  expand = '',
  onError,
}) {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // İlk paint'te "Kayıt bulunamadı" görünmesin diye true başlıyoruz; ilk fetch
  // (veya ready beklemesi) bitince false olur.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [viewMode, setViewMode] = useState('list');
  const [groupBy, setGroupBy] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [bucketLoadingByPath, setBucketLoadingByPath] = useState({});
  const [fetchedBucketPaths, setFetchedBucketPaths] = useState(() => new Set());
  const [bucketCounts, setBucketCounts] = useState({});
  // Sunucu bu listeyi görmeyi REDDETTİ (403/401). Boş sonuçtan ayrı tutulur: biri
  // "kayıt yok", diğeri "yetkin yok" demektir ve kullanıcıya farklı şey söylenmelidir.
  const [forbidden, setForbidden] = useState(false);

  // onError çoğunlukla inline arrow olarak geçilir (her render yeni kimlik).
  // Ref'e alıp reportError'ı sabit tut — aksi halde fetchData kimliği her render
  // değişir ve useEffect([fetchData]) sonsuz döngüye girer.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const reportError = useCallback((msg) => { if (onErrorRef.current) onErrorRef.current(msg); }, []);

  const effectiveGroupByList = useMemo(
    () => ((groupBy.length === 0 && viewMode === 'kanban' && defaultKanbanGroup) ? [defaultKanbanGroup] : groupBy),
    [groupBy, viewMode, defaultKanbanGroup],
  );
  const effectiveGroupBy = effectiveGroupByList[0] || 'none';
  const isGrouping = effectiveGroupByList.length > 0;
  const needsFullData = isGrouping || viewMode === 'kanban' || viewMode === 'chart';
  const effectivePageSize = needsFullData ? FULL_FETCH_PAGE_SIZE : pageSize;
  const effectiveCurrentPage = needsFullData ? 1 : currentPage;

  // Serbest metin + kolon filtrelerinden OData $filter string'i üret.
  const buildCurrentFilterParams = useCallback(() => {
    const clauses = [];
    if (baseFilter && baseFilter.trim()) {
      clauses.push(`(${baseFilter.trim()})`);
    }
    if (filterText.trim() && searchFields.length > 0) {
      const q = escapeODataString(filterText.trim()).toLowerCase();
      const ors = searchFields.map((f) => `contains(tolower(${f}),'${q}')`);
      clauses.push(`(${ors.join(' or ')})`);
    }
    for (const [field, filter] of Object.entries(columnFilters)) {
      const meta = columnFilterMeta[field];
      const clause = buildFilterClause(meta, filter);
      if (clause) clauses.push(clause);
    }
    return clauses.join(' and ');
  }, [baseFilter, filterText, columnFilters, searchFields, columnFilterMeta]);

  const fetchData = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const filterParams = buildCurrentFilterParams();
      // $select yalnız düz liste görünümünde (grup/kanban/chart tam veri ister).
      const select = (!needsFullData && selectFields.length) ? selectFields : undefined;
      const url = buildODataUrl(apiBase, { pageSize: effectivePageSize, currentPage: effectiveCurrentPage, sortField, sortDir, filterParams, select, expand: expand || undefined });
      const res = await fetch(url, { headers: authHeaders() });
      // YETKİSİZLİK (403), HATA DEĞİLDİR — ayrı ele alınır.
      //
      // 403 de "Veri alınamadı" diye raporlanınca ekran boş tablo + genel hata gösteriyordu;
      // kullanıcı bunu ARIZA sanıp destek kaydı açıyordu. Sunucunun "bu veriyi görme yetkin yok"
      // cevabı ile "veri çekilemedi" ayrılır ki ekran doğru mesajı gösterebilsin.
      //
      // 401 BİLEREK BURAYA DAHİL DEĞİL: o "oturum bitti" demektir ve authInterceptor onu global
      // olarak ele alır (token yenile → olmazsa çıkış). Burada "yetkiniz yok" diye göstermek,
      // süresi dolmuş oturumu yetki sorunu gibi gösterip kullanıcıyı yanlış yere yönlendirirdi.
      if (res.status === 403) {
        setForbidden(true);
        setRows([]);
        setTotalCount(0);
        return;
      }
      setForbidden(false);
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage: effectiveCurrentPage, pageSize: effectivePageSize, base: apiBase, filterParams, headers: authHeaders() }));
    } catch (error) {
      reportError(error.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [apiBase, ready, buildCurrentFilterParams, effectivePageSize, effectiveCurrentPage, sortField, sortDir, reportError, needsFullData, selectFields, expand]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // === Gruplama: bucket sayımları ($apply, tek istek) ===
  const fetchBucketCounts = useCallback(async () => {
    // Boş set ederken referansı koru (yeni {} her seferinde re-render tetiklerdi).
    const clearBuckets = () => setBucketCounts((prev) => (Object.keys(prev).length ? {} : prev));
    if (!isGrouping || (viewMode !== 'kanban' && viewMode !== 'list')) {
      clearBuckets();
      return;
    }
    const fields = effectiveGroupByList.filter((f) => groupByOdata[f]);
    if (fields.length === 0) { clearBuckets(); return; }

    const odataFields = fields.map((f) => groupByOdata[f].field);
    const baseFilter = buildCurrentFilterParams();
    const applyExpr = `groupby((${odataFields.join(',')}),aggregate($count as Count))`;
    let url = `${apiBase}?$apply=${encodeURIComponent(applyExpr)}`;
    if (baseFilter) url += `&$filter=${encodeURIComponent(baseFilter)}`;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) { setBucketCounts({}); return; }
      const data = await res.json();
      const rowsAgg = data?.value || [];
      const next = {};
      rowsAgg.forEach((agg) => {
        const parts = fields.map((f) => {
          const v = agg[groupByOdata[f].field];
          if (v === null || v === undefined) return '__none__';
          return String(v);
        });
        const c = Number(agg.Count) || 0;
        for (let i = 1; i <= parts.length; i += 1) {
          const path = parts.slice(0, i).join('::');
          next[path] = (next[path] || 0) + c;
        }
      });
      setBucketCounts(next);
    } catch {
      setBucketCounts({});
    }
  }, [apiBase, viewMode, isGrouping, effectiveGroupByList, groupByOdata, buildCurrentFilterParams]);

  useEffect(() => { fetchBucketCounts(); }, [fetchBucketCounts]);

  const buildPathFilterClause = useCallback((path, fields) => {
    const parts = path.split('::');
    const clauses = [];
    parts.forEach((k, i) => {
      const field = fields[i];
      const meta = groupByOdata[field];
      if (!meta) return;
      if (k === '__none__') {
        clauses.push(meta.type === 'string' ? `(${meta.field} eq null or ${meta.field} eq '')` : `${meta.field} eq null`);
      } else if (meta.type === 'number') {
        clauses.push(`${meta.field} eq ${k}`);
      } else if (meta.type === 'guid') {
        clauses.push(`${meta.field} eq ${k}`);
      } else {
        clauses.push(`${meta.field} eq '${escapeODataString(k)}'`);
      }
    });
    return clauses.join(' and ');
  }, [groupByOdata]);

  const fetchBucketLeafRows = useCallback(async (path) => {
    if (fetchedBucketPaths.has(path)) return;
    const baseFilter = buildCurrentFilterParams();
    const bucketFilter = buildPathFilterClause(path, effectiveGroupByList);
    const parts = [];
    if (baseFilter) parts.push(`(${baseFilter})`);
    if (bucketFilter) parts.push(bucketFilter);
    const combined = parts.join(' and ');

    setBucketLoadingByPath((prev) => ({ ...prev, [path]: true }));
    try {
      let url = `${apiBase}?$count=true&$top=500&$skip=0&$orderby=${encodeURIComponent(`${sortField} ${sortDir}`)}${combined ? `&$filter=${encodeURIComponent(combined)}` : ''}`;
      if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const fetchedRows = getODataRows(data);
      const c = getExplicitODataTotalCount(data);
      if (c != null) setBucketCounts((prev) => ({ ...prev, [path]: c }));
      setRows((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRows = fetchedRows.filter((r) => !existingIds.has(r.id));
        return newRows.length ? [...prev, ...newRows] : prev;
      });
      setFetchedBucketPaths((prev) => new Set(prev).add(path));
    } catch (e) {
      reportError('Bucket yüklenemedi: ' + (e.message || ''));
    } finally {
      setBucketLoadingByPath((prev) => { const next = { ...prev }; delete next[path]; return next; });
    }
  }, [apiBase, effectiveGroupByList, sortField, sortDir, fetchedBucketPaths, buildCurrentFilterParams, buildPathFilterClause, reportError, expand]);

  const toggleGroupExpand = useCallback((path) => {
    const pathStr = String(path);
    const wasExpanded = expandedGroups.has(pathStr);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(pathStr)) next.delete(pathStr); else next.add(pathStr);
      return next;
    });
    if (!wasExpanded) {
      const depth = pathStr.split('::').length;
      const isLeaf = depth >= effectiveGroupByList.length;
      if (isLeaf && !fetchedBucketPaths.has(pathStr)) {
        fetchBucketLeafRows(pathStr);
      }
    }
  }, [expandedGroups, effectiveGroupByList, fetchedBucketPaths, fetchBucketLeafRows]);

  // Filtre/gruplama/sıralama değişiminde genişletme & fetch durumunu sıfırla.
  useEffect(() => {
    setExpandedGroups(new Set());
    setFetchedBucketPaths(new Set());
  }, [effectiveGroupByList, filterText, columnFilters, sortField, sortDir]);

  // Tek-field flat bucket'lar (kanban için).
  const groupBuckets = useMemo(() => {
    if (!isGrouping || !getKeyLabel) return [];
    const groupField = effectiveGroupBy;
    const buckets = new Map();
    const ensure = (key, label) => {
      if (!buckets.has(key)) buckets.set(key, { key, label, rows: [] });
      return buckets.get(key);
    };
    if (seedKanbanBuckets) {
      (seedKanbanBuckets(groupField) || []).forEach(({ key, label }) => ensure(key, label));
    }
    rows.forEach((row) => {
      const { key, label } = getKeyLabel(groupField, row);
      ensure(key, label).rows.push(row);
    });
    const arr = Array.from(buckets.values());
    arr.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return String(a.label).localeCompare(String(b.label), 'tr', { sensitivity: 'base' });
    });
    return arr;
  }, [isGrouping, effectiveGroupBy, rows, getKeyLabel, seedKanbanBuckets]);

  // Multi-field nested bucket'lar (liste için).
  const nestedGroupBuckets = useMemo(() => {
    if (!isGrouping || !getKeyLabel) return [];
    const buildLevel = (rowsAtLevel, depth, parentPath) => {
      if (depth >= effectiveGroupByList.length) return null;
      const field = effectiveGroupByList[depth];
      const map = new Map();
      rowsAtLevel.forEach((row) => {
        const { key, label } = getKeyLabel(field, row);
        const bk = String(key);
        if (!map.has(bk)) {
          const path = parentPath ? `${parentPath}::${bk}` : bk;
          map.set(bk, { key, label, path, rows: [] });
        }
        map.get(bk).rows.push(row);
      });
      const arr = Array.from(map.values()).map((b) => ({
        ...b,
        children: buildLevel(b.rows, depth + 1, b.path),
      }));
      arr.sort((a, b) => {
        if (String(a.key) === '__none__') return 1;
        if (String(b.key) === '__none__') return -1;
        return String(a.label).localeCompare(String(b.label), 'tr', { sensitivity: 'base' });
      });
      return arr;
    };
    return buildLevel(rows, 0, '');
  }, [isGrouping, effectiveGroupByList, rows, getKeyLabel]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    if (rows.length >= totalCount) return;
    setLoadingMore(true);
    try {
      const filterParams = buildCurrentFilterParams();
      const skip = rows.length;
      const orderby = encodeURIComponent(`${sortField} ${sortDir}`);
      let url = `${apiBase}?$count=true&$top=${LOAD_MORE_BATCH}&$skip=${skip}&$orderby=${orderby}`;
      if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
      // loadMore yalnız düz liste görünümünde çalışır → $select uygulanır.
      if (selectFields.length) url += `&$select=${encodeURIComponent(selectFields.join(','))}`;
      if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const newRows = getODataRows(data);
      if (newRows.length > 0) setRows((prev) => [...prev, ...newRows]);
    } catch (e) {
      reportError('Yükleme hatası: ' + (e.message || ''));
    } finally {
      setLoadingMore(false);
    }
  }, [apiBase, rows.length, totalCount, sortField, sortDir, loadingMore, loading, buildCurrentFilterParams, reportError, selectFields, expand]);

  // Sıralama handler'ı (TanStack uyumlu, sayfa toGridSortField/toODataSortField ile sarar).
  const applySort = useCallback((odataField, dir) => {
    setCurrentPage(1);
    setSortField(odataField || defaultSortField);
    setSortDir(dir || 'asc');
  }, [defaultSortField]);

  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);

  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => {
      const copy = { ...prev };
      delete copy[columnId];
      return copy;
    });
    setCurrentPage(1);
  }, []);

  const applyFilterText = useCallback((text) => {
    setFilterText(text);
    setCurrentPage(1);
  }, []);

  // Tüm (filtreli) kayıtları çekip Excel'e aktar. cols: exportToExcel kolon tanımı.
  // `mapRow` opsiyoneldir: satırı Excel'e vermeden önce normalize etmek isteyen sayfalar
  // (örn. $expand'lı iade listesi → kalem/miktar/alış faturası türetmeleri) verir.
  // Mevcut filtre/sıralamaya uyan TÜM satırlar (sayfalama yok). Excel dışa aktarımı ve
  // Pivot bunun üzerine kurulur — pivot yalnız yüklü sayfayla çalışırsa "toplam" yanıltıcı olur.
  const fetchAllRows = useCallback(async (mapRow) => {
    const filterParams = buildCurrentFilterParams();
    const url = buildODataUrl(apiBase, { pageSize: 100000, currentPage: 1, sortField, sortDir, filterParams, expand: expand || undefined });
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error('Veri alınamadı');
    const raw = getODataRows(await res.json());
    return mapRow ? raw.map(mapRow) : raw;
  }, [apiBase, buildCurrentFilterParams, sortField, sortDir, expand]);

  const exportRows = useCallback(async (cols, fileName, mapRow) => {
    const allRows = await fetchAllRows(mapRow);
    if (allRows.length === 0) return 0;
    exportToExcel(allRows, cols, fileName);
    return allRows.length;
  }, [fetchAllRows]);

  return {
    // veri
    rows, setRows, totalCount, loading, loadingMore,
    // yetki — true ise liste boş DEĞİL, KAPALIDIR (bkz. fetchData 403 dalı)
    forbidden,
    // arama / filtre
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    // sıralama / sayfalama
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    // görünüm / gruplama
    viewMode, setViewMode, groupBy, setGroupBy,
    effectiveGroupByList, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets,
    // aksiyonlar
    fetchData, loadMore, exportRows, fetchAllRows, buildCurrentFilterParams,
  };
}
